/* eslint-disable camelcase */
/* eslint-disable import/prefer-default-export */
import { useGetStandardsBySyllabus } from '@apis/standards';
import { useCurrentSubject } from '@components/Component/Lesson/LessonContainer/hooks/query';
import {
  CurriculumSubjectRecord,
  StandardRecord,
} from '@components/ContentFilters/types';
import { useCourseById } from '@reducers/courses/hooks';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Node } from '@components/Atoms/MultiSelectTree/MultiSelectTree';

/**
 * Given an object map, recursively build a tree of nodes
 *
 * @param map
 * @param parentKey - The parent key used to build the id of non-standard nodes
 * @returns Node[]
 */
const getNodeFromMap = (map, parentKey = '') => {
  const nodes: Node[] = [];
  const mapKeys = Object.keys(map);

  // Skip displaying if there's only one node to avoid unnecessary nesting
  if (mapKeys.length === 1 && !map[mapKeys[0]]?.standard) {
    const currentKeyId = parentKey.length ? `${parentKey}.${mapKeys[0]}` : mapKeys[0];
    return getNodeFromMap(map[mapKeys[0]].children, currentKeyId);
  }

  mapKeys.forEach((key) => {
    // we'll use this to reference the current node's id for non-standard nodes
    const currentKeyId = parentKey.length ? `${parentKey}.${key}` : key;

    // if the current map object has 'standard', it's a standard node
    if ('standard' in map[key]) {
      nodes.push({
        ...map[key].standard,
        id: map[key].standard.id.toString(),
        label: `${map[key].standard.code}: ${map[key].standard.description}`,
        noChip: false,
        children: getNodeFromMap(map[key].children, currentKeyId),
      });

      // if it doesn't have 'standard', it's a non-standard node
    } else {
      nodes.push({
        ...map[key],
        id: currentKeyId,
        label: currentKeyId,
        children: getNodeFromMap(map[key].children, currentKeyId),
      });
    }
  });

  return nodes;
};

/**
 * Build a tree map from a list of standards records
 *
 * @param standards - The list of standards records to build the tree map from
 * @param subjects - The list of subjects to filter the standards by
 * @returns Node[]
 */
export const buildTreeMap = (standards: StandardRecord[], subjects: number[]) => {
  if (!standards || !Array.isArray(standards) || standards.length === 0) {
    return [];
  }

  const treeMap = standards.reduce((acc, standard) => {
    // filter by subjects
    if (
      subjects.length > 0 &&
      !standard.subjects.some((standard_subject) => subjects.includes(standard_subject))
    ) {
      return acc;
    }

    // now build the tree map
    const parts = standard.code.split('.');
    let current = acc;

    parts.forEach((part, index) => {
      if (!current[part]) {
        current[part] = {
          children: {},
          noChip: true, // no chip for non-standard nodes
          noCheckbox: index <= 2, // no checkbox for the first 3 levels
        };
      }

      // add the standard object and treat as standard node if we're at the last part
      if (index === parts.length - 1) {
        current[part].standard = standard;
        current[part].noChip = false;
        current[part].noCheckbox = false;
      }

      current = current[part].children;
    });

    return acc;
  }, {});

  return getNodeFromMap(treeMap);
};

export const useStandardSelector = (curriculumSubject?: CurriculumSubjectRecord[]) => {
  const { courseId } = useParams();
  const course = useCourseById(courseId);
  const { data: standards } = useGetStandardsBySyllabus(course?.syllabus);
  const currentSubject = useCurrentSubject();

  const subjects = useMemo(() => {
    if (currentSubject && currentSubject.subject_id) {
      return [currentSubject.subject_id];
    }

    if (curriculumSubject && curriculumSubject.length > 0) {
      return Array.from(new Set(curriculumSubject.map((cs) => cs.subject.id)));
    }

    return [];
  }, [curriculumSubject, currentSubject]);

  // create a map of standards for easy lookup
  const standardMap = useMemo(() => {
    if (!standards) {
      return [];
    }
    return standards.reduce((acc, standard) => {
      acc[standard.id] = standard;
      return acc;
    }, {});
  }, [standards]);

  // map to tree for use in MultiSelectTree
  const standardTree = useMemo(
    () => buildTreeMap(standards, subjects),
    [standards, subjects],
  );

  const standardAbGuidMap = useMemo(() => {
    if (!standards) {
      return {};
    }
    return standards.reduce((acc, standard) => {
      if (!standard?.ab_guid) {
        return acc;
      }
      acc[standard.ab_guid] = standard;
      return acc;
    }, {});
  }, [standards]);

  return {
    standardTree,
    standardMap,
    standardAbGuidMap,
  };
};
